import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import Swal from "sweetalert2";
import Provinces from "../../../enums/Provinces";
import { getMethod, postRequestByAuth } from "../../../function/helper";
import validateFormatNationalCode from "../../../function/validateFormatNationalCode";
import LoadingButton from "../../Loading/LoadingButton";
import styles from "./EditAddress.module.css";

export default function EditAddress({ onClose, onSubmit, defaultValues }) {
  const senderOrgetter = 1;

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues });

  const { isPending, mutate } = useMutation({
    mutationKey: ["EditAddress" + defaultValues.id],
    mutationFn: body =>
      postRequestByAuth("/address/v1/editAddress", body).then(res => {
        if (res && res.status === 200) {
          Swal.fire({
            icon: "success",
            text: res.data.message,
            showConfirmButton: true,
            confirmButtonText: "متوجه شدم",
          });
          onSubmit();
          return;
        }

        Swal.fire({
          icon: "error",
          text: res.message,
          showConfirmButton: true,
          confirmButtonText: "متوجه شدم",
        });
      }),
  });

  const [cities, setCities] = useState([{ value: 0, label: "..." }]);
  const [isLoading, setIsLoading] = useState(false);

  const getCities = async id => {
    setIsLoading(true);
    const res = await getMethod("/address/v1/city/show/" + id);
    setIsLoading(false);

    const cityArray = [{ value: 0, label: "..." }];
    if (res && res.status === 200) {
      res.data.forEach(item =>
        cityArray.push({ value: item.id, label: item.faName })
      );
    }
    setCities(cityArray);
  };

  useEffect(() => {
    getCities(defaultValues.provinceId);
  }, [defaultValues.provinceId]);

  return (
    <Fragment>
      <Modal
        show
        size="lg"
        centered
        className={classNames("rtl-all", styles.modal)}
      >
        <form onSubmit={handleSubmit(mutate)}>
          <Modal.Header className="bg-white">
            <h5 className="m-0">ویرایش آدرس</h5>
          </Modal.Header>

          <Modal.Body className="bg-white">
            <div className="d-flex flex-wrap align-items-start">
              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>نوع آدرس</Form.Label>
                <Form.Select
                  disabled={typeof senderOrgetter === "number"}
                  {...register("senderOrgetter", {
                    value: senderOrgetter,
                    valueAsNumber: true,
                  })}
                >
                  <option value={0}>فرستنده</option>
                  <option value={1}>گیرنده</option>
                </Form.Select>
              </Col>

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>نوع شخص</Form.Label>
                <Form.Select
                  {...register("type", { setValueAs: v => parseInt(v) })}
                >
                  <option value={0}>حقیقی</option>
                  <option value={1}>حقوقی</option>
                </Form.Select>
              </Col>

              {watch("type") === 0 && (
                <Fragment>
                  <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                    <Form.Label>
                      نام
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register("name", {
                        required: true,
                        minLength: 3,
                        maxLength: 50,
                      })}
                    />
                    {errors.name?.type === "required" && <p>نام الزامی</p>}
                    {errors.name?.type === "minLength" && (
                      <p>حداقل 3 کاراکتر</p>
                    )}
                    {errors.name?.type === "maxLength" && (
                      <p>حداکثر 50 کاراکتر</p>
                    )}
                  </Col>

                  <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                    <Form.Label>
                      نام خانوادگی
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register("family", { required: true })}
                    />
                    {errors.family?.type === "required" && (
                      <p>نام خانوادگی الزامی</p>
                    )}
                  </Col>

                  <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                    <Form.Label>کد ملی</Form.Label>
                    <Form.Control
                      type="number"
                      {...register("nationalCode", {
                        validate: {
                          length: v => {
                            if (!v) {
                              return true;
                            }
                            if (v.length === 10) {
                              return true;
                            }
                            return "کد ملی باید 10 رقم باشد";
                          },

                          format: v => {
                            if (!v) {
                              return true;
                            }
                            if (validateFormatNationalCode(v)) {
                              return true;
                            }
                            return "فرمت کد ملی صحیح نمی‌باشد";
                          },
                        },
                      })}
                    />
                    {errors.nationalCode && (
                      <p>{errors.nationalCode.message}</p>
                    )}
                  </Col>
                </Fragment>
              )}

              {watch("type") === 1 && (
                <Fragment>
                  <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                    <Form.Label>
                      نام شرکت
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register("compony", { required: true })}
                    />
                    {errors.compony?.type === "required" && (
                      <p>نام شرکت الزامی</p>
                    )}
                  </Col>

                  <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                    <Form.Label>شناسه ملی شرکت</Form.Label>
                    <Form.Control type="number" {...register("companyCode")} />
                  </Col>
                </Fragment>
              )}

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>
                  تلفن همراه<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  {...register("phone", {
                    required: true,
                    minLength: 11,
                    maxLength: 11,
                    validate: {
                      format: v => {
                        if (v[0] !== "0" || v[1] !== "9") {
                          return false;
                        }

                        return true;
                      },
                    },
                  })}
                />
                {errors.phone?.type === "required" && <p>تلفن همراه الزامی</p>}
                {errors.phone?.type === "minLength" && <p>باید 11 رقم باشد</p>}
                {errors.phone?.type === "maxLength" && <p>باید 11 رقم باشد</p>}
                {errors.phone?.type === "format" && (
                  <p>تلفن همراه باید با 09 شروع شود</p>
                )}
              </Col>

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>تلفن ثابت</Form.Label>
                <Form.Control type="number" {...register("tel")} />
              </Col>

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>ایمیل</Form.Label>
                <Form.Control
                  type="text"
                  {...register("email", {
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: "باید قالب ایمیل رعایت شود",
                    },
                  })}
                />
                {errors.email?.type === "pattern" && (
                  <p>{errors.email.message}</p>
                )}
              </Col>

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>
                  استان<span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  {...register("provinceId", {
                    valueAsNumber: true,
                    min: 1,
                    onChange: e => {
                      getCities(e.target.value);
                      setValue("cityId", 0);
                    },
                  })}
                >
                  <option value={0}>هیچکدام</option>
                  {Object.keys(Provinces).map(item => (
                    <option value={item} key={item}>
                      {Provinces[item]}
                    </option>
                  ))}
                </Form.Select>
                {errors.provinceId?.type === "min" && (
                  <p>انتخاب استان الزامی</p>
                )}
              </Col>

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>
                  شهر<span className="text-danger">*</span>
                </Form.Label>

                <Controller
                  control={control}
                  defaultValue={0}
                  rules={{ required: true, min: 1 }}
                  name="cityId"
                  render={({ field: { onChange, value, ref } }) => (
                    <Select
                      inputRef={ref}
                      classNamePrefix="dir-farsi "
                      options={cities}
                      value={cities.find(c => c.value === value)}
                      onChange={val => onChange(val.value)}
                    />
                  )}
                />
                {errors.cityId?.type === "min" && <p>انتخاب شهر الزامی</p>}
              </Col>

              <Col xs={12} sm={6} lg={4} className="py-1 px-2">
                <Form.Label>
                  <span>کد پستی</span>
                  {senderOrgetter === 1 && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Form.Control
                  type="number"
                  {...register("postCode", {
                    required: {
                      value: true,
                      message: "کد پستی الزامی",
                    },
                    minLength: {
                      value: 10,
                      message: "کد پستی باید 10 رقم باشد",
                    },
                    maxLength: {
                      value: 10,
                      message: "کد پستی باید 10 رقم باشد",
                    },
                  })}
                />
                {errors.postCode && <p>{errors.postCode.message}</p>}
              </Col>

              <Col xs={12} className="py-1 px-2">
                <Form.Label>
                  آدرس<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  {...register("address", { required: true, minLength: 5 })}
                />
                {errors.address?.type === "required" && <p>آدرس الزامی</p>}
                {errors.address?.type === "minLength" && <p>حداقل 5 حرف</p>}
              </Col>
            </div>

            <p className="mb-0 mt-4 text-dark" style={{ textAlign: "justify" }}>
              توجه داشته باشید که آدرس برای مرسولاتی که قبلا ثبت کردید تغییر
              نمی‌کند و ویرایش فقط برای مرسولاتی جدیدی که ثبت می‌کنید اعمال
              می‌شود.
            </p>
          </Modal.Body>

          <Modal.Footer className="bg-white">
            {isPending && (
              <LoadingButton
                center
                className="btn-success"
                title="در حال ذخیره"
              />
            )}

            {!isPending && (
              <Button
                variant="success"
                disabled={isLoading}
                type="submit"
                className="d-flex align-items-center"
              >
                <span>ذخیره</span>
              </Button>
            )}

            <Button
              type="button"
              variant="danger"
              disabled={isLoading || isPending}
              onClick={onClose}
            >
              بستن
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </Fragment>
  );
}
